import { useCallback, useEffect, useRef, useState } from 'react';
import { createDocumentCollaborationClient } from './createDocumentCollaborationClient';
import type { DocumentCollaborationClient } from './createDocumentCollaborationClient';
import type {
  DocumentEditLockHolder,
  DocumentFileSavedNotice,
  DocumentRosterParticipant
} from './documentCollaborationTypes';
import {
  formatEditLockHolderName,
  isEditLockHeldBySelf
} from './documentCollaborationTypes';

export interface UseDocumentPresenceOptions {
  mountId: string;
  path: string;
  enabled?: boolean;
  currentUserId?: string | null;
  /** Extra identity keys (UPN / mail / object id) for matching the edit-lock holder to this user. */
  currentUserAliases?: Iterable<string> | null;
  /** Fired for every peer save-back (after the version stamp lands in storage). */
  onFileSaved?: (notice: DocumentFileSavedNotice) => void;
  /** Fired when another user holds the exclusive edit lock (toast / block input). */
  onPeerEditLock?: (holder: DocumentEditLockHolder) => void;
  /** Fired when this client tried to edit but the lock was denied. */
  onEditLockDenied?: (holder: DocumentEditLockHolder | null) => void;
}

export interface DocumentPresenceState {
  roster: DocumentRosterParticipant[];
  isConnected: boolean;
  /** Current exclusive edit lock holder, or null when unlocked. */
  editLockHolder: DocumentEditLockHolder | null;
  /** True when this client holds the exclusive edit lock. */
  isEditLockHolder: boolean;
  /** True when another user holds the lock (viewers should block input). */
  isPeerEditLocked: boolean;
  notifyEditing: (isEditing: boolean) => Promise<boolean>;
  notifyTyping: () => void;
}

/**
 * Subscribes to the document collaboration hub for one file. Returns the live roster, exclusive
 * edit lock, and connection state.
 */
export function useDocumentPresence({
  mountId,
  path,
  enabled = true,
  currentUserId = null,
  currentUserAliases = null,
  onFileSaved,
  onPeerEditLock,
  onEditLockDenied
}: UseDocumentPresenceOptions): DocumentPresenceState {
  const [roster, setRoster] = useState<DocumentRosterParticipant[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [editLockHolder, setEditLockHolder] = useState<DocumentEditLockHolder | null>(null);
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const onFileSavedRef = useRef(onFileSaved);
  const onPeerEditLockRef = useRef(onPeerEditLock);
  const onEditLockDeniedRef = useRef(onEditLockDenied);
  const currentUserIdRef = useRef(currentUserId);
  const currentUserAliasesRef = useRef(currentUserAliases);
  const clientRef = useRef<DocumentCollaborationClient | null>(null);
  const lastPeerToastKeyRef = useRef<string | null>(null);
  onFileSavedRef.current = onFileSaved;
  onPeerEditLockRef.current = onPeerEditLock;
  onEditLockDeniedRef.current = onEditLockDenied;
  currentUserIdRef.current = currentUserId;
  currentUserAliasesRef.current = currentUserAliases;

  const notifyEditing = useCallback(async (isEditing: boolean) => {
    return (await clientRef.current?.notifyEditing(isEditing)) ?? false;
  }, []);

  const notifyTyping = useCallback(() => {
    clientRef.current?.notifyTyping();
  }, []);

  useEffect(() => {
    if (!enabled || !mountId || !path) {
      clientRef.current = null;
      setRoster([]);
      setIsConnected(false);
      setEditLockHolder(null);
      setConnectionId(null);
      lastPeerToastKeyRef.current = null;
      return;
    }

    setRoster([]);
    setEditLockHolder(null);
    setConnectionId(null);
    lastPeerToastKeyRef.current = null;

    const client = createDocumentCollaborationClient({
      mountId,
      path,
      onRosterChanged: setRoster,
      onFileSaved: (notice) => onFileSavedRef.current?.(notice),
      onEditLockChanged: (holder) => {
        setEditLockHolder(holder);
        setConnectionId(clientRef.current?.getConnectionId() ?? null);
        if (!holder) {
          lastPeerToastKeyRef.current = null;
          return;
        }
        if (
          isEditLockHeldBySelf(holder, {
            connectionId: clientRef.current?.getConnectionId(),
            currentUserId: currentUserIdRef.current,
            currentUserAliases: currentUserAliasesRef.current
          })
        ) {
          return;
        }
        const key = `${holder.userId}:${holder.connectionId}`;
        if (lastPeerToastKeyRef.current === key) return;
        lastPeerToastKeyRef.current = key;
        onPeerEditLockRef.current?.(holder);
      },
      onEditLockDenied: (holder) => {
        onEditLockDeniedRef.current?.(holder);
      },
      onConnectedChange: (connected) => {
        setIsConnected(connected);
        setConnectionId(clientRef.current?.getConnectionId() ?? null);
      }
    });
    clientRef.current = client;

    void client.start().then(() => {
      setConnectionId(client.getConnectionId());
    });
    return () => {
      clientRef.current = null;
      void client.stop();
    };
    // Identity must NOT recreate the hub connection — profile loads async after mount and a
    // reconnect would release an edit lock the user just acquired.
  }, [enabled, mountId, path]);

  const isEditLockHolder = isEditLockHeldBySelf(editLockHolder, {
    connectionId,
    currentUserId,
    currentUserAliases
  });
  const isPeerEditLocked = Boolean(editLockHolder && !isEditLockHolder);

  return {
    roster,
    isConnected,
    editLockHolder,
    isEditLockHolder,
    isPeerEditLocked,
    notifyEditing,
    notifyTyping
  };
}

export function peerEditLockToastMessage(holder: DocumentEditLockHolder | null | undefined): string {
  const name = formatEditLockHolderName(holder);
  return `${name} is editing. Wait until they click Stop Editing.`;
}
